import { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import auth from '../service/auth';
import { PageHeader, Card, StatCard, Spinner, Badge, Select, Button } from '../components/ui';

const EVENT_LABELS = {
  OPEN_APP: "Actifs",
  OFFLINE_APP: "Inactifs",
  UNINSTALLED_APP: "Désinstallés",
  CAMPAIGN_PUSH: "Notifications envoyées",
  OPEN_BY_NOTIF: "Ouvertures via notif",
};

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [pushing, setPushing] = useState(null);

  const load = async () => {
    const [dash, lists] = await Promise.all([auth.api('dashboard'), auth.api('lists')]);
    setStats(dash);
    setApps(lists.apps || []);
    if (!selectedApp && lists.apps?.length) setSelectedApp(String(lists.apps[0].app_id));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const push = async (campaign_id) => {
    if (!window.confirm("Envoyer les notifications de cette campagne maintenant ?")) return;
    setPushing(campaign_id);
    await auth.api('campaign/' + campaign_id + '/push');
    await load();
    setPushing(null);
  };

  if (!stats) return <Spinner />;

  const { totals, appEvents, appTokens, campaignEvents, eventsByDay, ticketsByStatus } = stats;

  // --- Courbe d'activité 30 jours ---
  const activityOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: 40, right: 16, top: 30, bottom: 30 },
    xAxis: {
      type: 'category',
      data: eventsByDay.map((d) => new Date(d.day).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })),
    },
    yAxis: { type: 'value' },
    series: [{
      name: "Événements",
      data: eventsByDay.map((d) => d.count),
      type: 'line',
      smooth: true,
      areaStyle: { opacity: 0.15 },
      itemStyle: { color: '#6366f1' },
    }],
  };

  // --- Donut statuts tickets ---
  const ticketsOption = {
    tooltip: { trigger: 'item' },
    legend: { bottom: 0 },
    series: [{
      type: 'pie',
      radius: ['45%', '70%'],
      data: ticketsByStatus.map((t) => ({ name: t.status, value: t.count })),
      color: ['#10b981', '#f43f5e', '#f59e0b', '#6366f1'],
    }],
  };

  // --- Donut événements de l'app sélectionnée ---
  const appEventRows = appEvents.filter((e) => String(e.app_id) === String(selectedApp));
  const appOption = {
    tooltip: { trigger: 'item' },
    legend: { bottom: 0 },
    series: [{
      type: 'pie',
      radius: ['45%', '70%'],
      data: appEventRows.map((e) => ({ name: EVENT_LABELS[e.name] || e.name, value: e.count })),
      color: ['#6366f1', '#f59e0b', '#f43f5e', '#10b981', '#8b5cf6'],
    }],
  };

  // --- Engagement par campagne (envois vs ouvertures) ---
  const campaignIds = [...new Set(campaignEvents.map((c) => c.campaign_id))];
  const campaignNames = campaignIds.map((id) => {
    const row = campaignEvents.find((c) => c.campaign_id === id);
    return { id, name: row?.campaign_name || `#${id}` };
  }).slice(-10); // les 10 dernières
  const countFor = (id, name) => campaignEvents.find((c) => c.campaign_id === id && c.name === name)?.count || 0;
  const engagementOption = {
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0 },
    grid: { left: 40, right: 16, top: 30, bottom: 60 },
    xAxis: {
      type: 'category',
      data: campaignNames.map((c) => c.name.length > 18 ? c.name.slice(0, 18) + "…" : c.name),
      axisLabel: { rotate: 25 },
    },
    yAxis: { type: 'value' },
    series: [
      { name: "Envois", type: 'bar', data: campaignNames.map((c) => countFor(c.id, 'CAMPAIGN_PUSH')), itemStyle: { color: '#6366f1' } },
      { name: "Ouvertures", type: 'bar', data: campaignNames.map((c) => countFor(c.id, 'OPEN_BY_NOTIF')), itemStyle: { color: '#10b981' } },
    ],
  };

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Vue d'ensemble de la plateforme de notifications" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Applications" value={totals.apps} icon="📱" accent="indigo" />
        <StatCard label="Tokens" value={totals.tokens} icon="🔑" accent="emerald" />
        <StatCard label="Campagnes" value={totals.campaigns} icon="📣" accent="amber" />
        <StatCard label="Tickets" value={totals.tickets} icon="🎫" accent="rose" />
        <StatCard label="Événements" value={totals.events} icon="⚡" accent="indigo" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-semibold text-slate-800 mb-2">Activité — 30 derniers jours</h3>
          <ReactECharts option={activityOption} style={{ height: '280px' }} notMerge lazyUpdate />
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-2">Tickets Expo par statut</h3>
          <ReactECharts option={ticketsOption} style={{ height: '280px' }} notMerge lazyUpdate />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-2 gap-2">
            <h3 className="font-semibold text-slate-800">Événements par app</h3>
            <Select value={selectedApp} onChange={(e) => setSelectedApp(e.target.value)}>
              {apps.map((a) => <option key={a.app_id} value={a.app_id}>{a.name}</option>)}
            </Select>
          </div>
          <ReactECharts option={appOption} style={{ height: '280px' }} notMerge lazyUpdate />
        </Card>
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-semibold text-slate-800 mb-2">Engagement des 10 dernières campagnes</h3>
          <ReactECharts option={engagementOption} style={{ height: '280px' }} notMerge lazyUpdate />
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Tokens par application</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {appTokens.map((a) => (
            <div key={a.app_id} className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
              <span className="text-sm font-medium text-slate-700 truncate">{a.app_name || `App #${a.app_id}`}</span>
              <Badge color="indigo">{Number(a.count).toLocaleString("fr-FR")} tokens</Badge>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 mt-4">
        <h3 className="font-semibold text-slate-800 mb-4">Engagement par campagne</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="px-3 py-2 font-medium">Campagne</th>
                <th className="px-3 py-2 font-medium text-right">Envois</th>
                <th className="px-3 py-2 font-medium text-right">Ouvertures</th>
                <th className="px-3 py-2 font-medium text-right">Taux</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {[...new Set(campaignEvents.map((c) => c.campaign_id))].map((id) => {
                const row = campaignEvents.find((c) => c.campaign_id === id);
                const sent = countFor(id, 'CAMPAIGN_PUSH');
                const opened = countFor(id, 'OPEN_BY_NOTIF');
                const rate = sent ? Math.round((100 * opened) / sent) : 0;
                return (
                  <tr key={id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">{row?.campaign_name || `#${id}`}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{sent.toLocaleString("fr-FR")}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{opened.toLocaleString("fr-FR")}</td>
                    <td className="px-3 py-2 text-right">
                      <Badge color={rate >= 30 ? "green" : rate >= 10 ? "amber" : "red"}>{rate}%</Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        className="!px-2.5 !py-1 text-xs"
                        disabled={pushing === id}
                        onClick={() => push(id)}
                      >
                        {pushing === id ? "Envoi…" : "🚀 Push"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default Dashboard;
